/**
 * CLI feature-flag lifecycle and oven management.
 */

import process from "node:process";
import { readFileSync } from "node:fs";
import { getArgValue, hasFlag } from "./auth.js";
import {
  apiRequest,
  fetchList,
  parseLifecycleStage,
  toLifecycleLabel,
  type LifecycleStage,
} from "./cli-helpers.js";

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_REVIEW_INTERVAL_DAYS = 7;
const DEFAULT_MAX_WINDOW_DAYS = 30;

type LifecycleFlag = {
  id?: string;
  name?: string;
  lifecycleStage: LifecycleStage;
  finalValue: boolean | null;
  bakeMonitoringPlan?: string | null;
  bakeStartedAt?: string | null;
  bakeReviewIntervalDays?: number | null;
  bakeMaxWindowDays?: number | null;
};

type FlagApiResponse = {
  ok: boolean;
  error?: string;
  flag?: LifecycleFlag;
};

type OvenFlag = {
  id: string;
  name: string;
  description: string | null;
  productCardId: string | null;
  productTitle: string | null;
  bakeMonitoringPlan: string | null;
  bakeStartedAt: string | null;
  bakeReviewIntervalDays: number;
  bakeMaxWindowDays: number;
  bakeAgeDays: number;
  burning: boolean;
};

/** Resolve a `--plan @file.md` (read file) or inline string value. */
function readContent(value: string): string {
  if (value.startsWith("@")) {
    return readFileSync(value.slice(1), "utf8");
  }
  return value;
}

/** Bake age (days) + burning state from a flag's bake metadata. */
function bakeStatus(flag: {
  bakeStartedAt?: string | null;
  bakeMaxWindowDays?: number | null;
}): { ageDays: number; burning: boolean } {
  if (!flag.bakeStartedAt) return { ageDays: 0, burning: false };
  const startedMs = new Date(flag.bakeStartedAt).getTime();
  const ageMs = Date.now() - startedMs;
  const maxWindow = flag.bakeMaxWindowDays ?? DEFAULT_MAX_WINDOW_DAYS;
  return { ageDays: Math.floor(ageMs / DAY_MS), burning: ageMs > maxWindow * DAY_MS };
}

/** ` baking 4d 🔥` style suffix for list rows. */
export function formatBakeSuffix(flag: {
  bakeStartedAt?: string | null;
  bakeMaxWindowDays?: number | null;
}): string {
  const { ageDays, burning } = bakeStatus(flag);
  return ` baked=${ageDays}d${burning ? " 🔥BURNING" : ""}`;
}

export async function runLifecycle(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const name = argv[0];

  if (!name || name.startsWith("--")) {
    console.error("Error: Missing flag name.");
    console.error(
      'Usage: canary feature-flag lifecycle <name> --stage <active|baking|internal|deprecated|ready_for_cleanup> [--final-value true|false] [--plan @file|"text"] [--review-interval <days>] [--max-window <days>]'
    );
    process.exit(1);
  }

  const stage = parseLifecycleStage(argv);
  const rawFinalValue = getArgValue(argv, "--final-value");
  const clearFinalValue = hasFlag(argv, "--clear-final-value");

  // active, baking and internal are gate-driven (no final value); deprecated
  // and ready_for_cleanup carry a forced final value.
  const gateDriven = stage === "active" || stage === "baking" || stage === "internal";

  if (rawFinalValue !== undefined && clearFinalValue) {
    console.error("Error: use either --final-value or --clear-final-value, not both.");
    process.exit(1);
  }

  let finalValue: boolean | undefined = undefined;

  if (gateDriven) {
    if (rawFinalValue !== undefined) {
      console.error(`Error: ${stage} stage does not accept --final-value.`);
      process.exit(1);
    }
    finalValue = undefined;
  } else {
    if (clearFinalValue) {
      console.error("Error: --clear-final-value can only be used with --stage active.");
      process.exit(1);
    }
    if (!rawFinalValue) {
      console.error("Error: --final-value true|false is required for deprecated or ready_for_cleanup.");
      process.exit(1);
    }
    if (rawFinalValue !== "true" && rawFinalValue !== "false") {
      console.error("Error: --final-value must be true or false.");
      process.exit(1);
    }
    finalValue = rawFinalValue === "true";
  }

  // Oven (baking) options: monitoring plan + review cadence.
  let monitoringPlan: string | undefined;
  let reviewIntervalDays: number | undefined;
  let maxWindowDays: number | undefined;
  if (stage === "baking") {
    const rawPlan = getArgValue(argv, "--plan");
    if (rawPlan !== undefined) monitoringPlan = readContent(rawPlan);
    const rawInterval = getArgValue(argv, "--review-interval");
    if (rawInterval !== undefined) {
      reviewIntervalDays = Number.parseInt(rawInterval, 10);
      if (!Number.isInteger(reviewIntervalDays) || reviewIntervalDays <= 0) {
        console.error("Error: --review-interval must be a positive integer (days).");
        process.exit(1);
      }
    }
    const rawWindow = getArgValue(argv, "--max-window");
    if (rawWindow !== undefined) {
      maxWindowDays = Number.parseInt(rawWindow, 10);
      if (!Number.isInteger(maxWindowDays) || maxWindowDays <= 0) {
        console.error("Error: --max-window must be a positive integer (days).");
        process.exit(1);
      }
    }
  }

  const result = await apiRequest<FlagApiResponse>(
    apiUrl,
    token,
    "POST",
    `/superadmin/feature-flags/${encodeURIComponent(name)}/lifecycle`,
    { stage, finalValue, monitoringPlan, reviewIntervalDays, maxWindowDays }
  );

  if (!result.ok || !result.flag) {
    console.error(`Error: ${result.error ?? "Failed to update lifecycle"}`);
    process.exit(1);
  }

  if (result.flag.lifecycleStage === "baking") {
    const { ageDays } = bakeStatus(result.flag);
    console.log(
      `Put ${name} in the oven: stage=baking, baked=${ageDays}d, review every ${result.flag.bakeReviewIntervalDays ?? DEFAULT_REVIEW_INTERVAL_DAYS}d, burns after ${result.flag.bakeMaxWindowDays ?? DEFAULT_MAX_WINDOW_DAYS}d.`
    );
    return;
  }

  const final =
    result.flag.lifecycleStage === "active" || result.flag.lifecycleStage === "internal"
      ? "(none)"
      : result.flag.finalValue === true
        ? "true"
        : "false";
  console.log(
    `Updated lifecycle for ${name}: stage=${toLifecycleLabel(result.flag.lifecycleStage)}, final=${final}`
  );
}

export async function runOven(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const jsonOutput = hasFlag(argv, "--json");
  const flags = await fetchList<OvenFlag>(
    apiUrl,
    token,
    "/superadmin/feature-flags/oven",
    "flags"
  );

  if (jsonOutput) {
    console.log(JSON.stringify(flags, null, 2));
    return;
  }

  if (flags.length === 0) {
    console.log("The oven is empty — no flags are baking.");
    return;
  }

  console.log(`In the oven (${flags.length}):`);
  for (const flag of flags) {
    const burn = flag.burning ? " 🔥BURNING" : "";
    const product = flag.productCardId ? ` product=${flag.productTitle ?? flag.productCardId}` : "";
    console.log(
      `  ${flag.name}  baked=${flag.bakeAgeDays}d (review ${flag.bakeReviewIntervalDays}d / burns ${flag.bakeMaxWindowDays}d)${burn}${product}`
    );
    if (flag.bakeMonitoringPlan) {
      const firstLine = flag.bakeMonitoringPlan.split("\n")[0]?.slice(0, 100) ?? "";
      console.log(`      plan: ${firstLine}`);
    }
  }
}
